/**
 * AnswerButtonOverUnder - Specialized answer button for over/under questions
 *
 * Figma CD over-under buttons (10203:60669 default / 10203:60905 active):
 * [arrow | label] … [vote %]
 */

import React from 'react';
import { OptionPercentageStat } from '../OptionPercentageStat';
import { LockBadge } from './LockBadge';
import { getPickOptionTextClass, getPickResultSurfaceClass, hasPickResult } from './pickResultStyles';
import {
  getPickLockedBackgroundClass,
  getPickLockedBorderClass,
  getPickLockedLabelClass,
} from './pickLockedStyles';

interface AnswerButtonOverUnderProps {
  answer: 'over' | 'under';
  label: string;
  isSelected: boolean;
  percentage?: number;
  onClick: () => void;
  disabled?: boolean;
  locked?: boolean;
  isCorrect?: boolean | null;
  testId?: string;
  isDailyDouble?: boolean;
  className?: string;
}

const ARROW_UP = '↑';
const ARROW_DOWN = '↓';

export const AnswerButtonOverUnder: React.FC<AnswerButtonOverUnderProps> = ({
  answer,
  label,
  isSelected,
  percentage,
  onClick,
  disabled = false,
  locked = false,
  isCorrect,
  testId,
  className = '',
}) => {
  const hasResult = hasPickResult(isCorrect);
  const resultSurfaceClass = getPickResultSurfaceClass(isSelected, isCorrect);
  const labelClass =
    locked && isSelected
      ? getPickLockedLabelClass()
      : getPickOptionTextClass(isSelected, hasResult);
  const showLockBadge = locked && isSelected && !hasResult;

  const getBackgroundClass = () => {
    if (resultSurfaceClass) return resultSurfaceClass;
    if (locked) return getPickLockedBackgroundClass(isSelected);
    if (isSelected) return 'bg-white';
    return 'bg-surface-muted';
  };

  const getBorderClass = () => {
    if (resultSurfaceClass) return '';
    if (locked) return getPickLockedBorderClass(isSelected);
    return '';
  };

  const arrowIcon = answer === 'over' ? ARROW_UP : ARROW_DOWN;

  return (
    <button
      type="button"
      data-testid={testId || `over-under-answer-${answer}`}
      data-test-result={isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
      data-selected={isSelected ? 'true' : 'false'}
      data-locked={locked ? 'true' : undefined}
      onClick={() => {
        if (!disabled && !locked) onClick();
      }}
      disabled={disabled || locked}
      className={`
        group peer w-full flex items-center gap-3 px-3 py-4 rounded-[6px] transition-smooth relative
        ${getBackgroundClass()}
        ${getBorderClass()}
        ${disabled || locked ? 'cursor-not-allowed' : ''}
        ${disabled && !hasResult && !locked ? 'opacity-90' : ''}
        ${className}
      `}
    >
      <div className="flex flex-1 items-center gap-1.5 min-w-0">
        <span
          className={`shrink-0 size-4 flex items-center justify-center font-sans font-semibold text-label-lg-bold ${labelClass}`}
          aria-hidden
        >
          {arrowIcon}
        </span>
        <span
          className={`font-sans font-semibold capitalize text-label-lg-bold truncate ${labelClass}`}
        >
          {label}
        </span>
      </div>

      {percentage !== undefined && !showLockBadge ? (
        <div className="shrink-0 w-7">
          <OptionPercentageStat
            percentage={percentage}
            isSelected={isSelected && !hasResult && !locked}
          />
        </div>
      ) : null}

      {showLockBadge && (
        <LockBadge data-testid={`lock-badge-${answer}`} className="shrink-0" />
      )}
    </button>
  );
};
